import mixpanel from 'mixpanel-browser'

const token = import.meta.env.VITE_MIXPANEL_TOKEN

// ---------- Helpers ----------

function generateClientId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return 'c_' + crypto.randomUUID()
  }
  // Fallback for older browsers (Safari < 15.4 etc.)
  return 'c_' + 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function safeStorageGet(key) {
  try { return localStorage.getItem(key) } catch (e) { return null }
}

function safeStorageSet(key, value) {
  try { localStorage.setItem(key, value); return true } catch (e) { return false }
}

// ---------- Bootstrap ----------

function bootstrapIdentity(mp) {
  // 1. Stable client identity (anonymous, persists across sessions)
  let clientId = safeStorageGet('saleem_client_id')
  if (!clientId) {
    clientId = generateClientId()
    safeStorageSet('saleem_client_id', clientId)
  }
  mp.identify(clientId)

  // 2. Current-session attribution (super-properties, sent on every event)
  const params = new URLSearchParams(window.location.search)
  mp.register({
    user_type: 'patient',
    entry_path: window.location.pathname,
    entry_url: window.location.href,
    ref: params.get('ref') || null,
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
    utm_term: params.get('utm_term') || null,
    referring_doctor_id: params.get('doctor') || null,
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'production',
  })

  // 3. First-touch attribution (user profile, set once, never overwritten)
  const firstTouch = {
    first_entry_path: window.location.pathname,
    first_entry_url: window.location.href,
    first_seen_at: new Date().toISOString(),
  }
  if (params.get('ref')) firstTouch.first_ref = params.get('ref')
  if (params.get('utm_source')) firstTouch.first_utm_source = params.get('utm_source')
  if (params.get('utm_medium')) firstTouch.first_utm_medium = params.get('utm_medium')
  if (params.get('utm_campaign')) firstTouch.first_utm_campaign = params.get('utm_campaign')
  if (params.get('doctor')) firstTouch.first_referring_doctor_id = params.get('doctor')
  mp.people.set_once(firstTouch)

  // 4. Site Entry event, once per session, anchors the Universal First-Touch funnel
  try {
    if (!sessionStorage.getItem('saleem_session_entry_fired')) {
      mp.track('Site Entry', {
        entry_path: window.location.pathname,
        entry_url: window.location.href,
      })
      sessionStorage.setItem('saleem_session_entry_fired', '1')
    }
  } catch (e) {
    mp.track('Site Entry', {
      entry_path: window.location.pathname,
      entry_url: window.location.href,
    })
  }
}

// ---------- Init ----------

// Browser only: the server render imports this module too, and there is no visitor to track there.
const inBrowser = typeof window !== 'undefined'

if (token && inBrowser) {
  mixpanel.init(token, {
    api_host: 'https://api-eu.mixpanel.com',
    debug: false,
    persistence: 'localStorage',
    // Do NOT record every visitor — that was the entire Mixpanel bill (45k+ landing
    // bounces watching nothing). Replay is started on demand when a visitor enters a
    // money/critical flow — see startReplay() + useMixpanel().enterFlow().
    record_sessions_percent: 0,
    record_heatmap_data: true,
    loaded: (mp) => bootstrapIdentity(mp),
  })
} else if (inBrowser) {
  console.warn('Mixpanel token is missing, analytics is disabled.')
}

export const mixpanelEnabled = !!token && inBrowser
mixpanel.__enabled = mixpanelEnabled

// Session replay is started on demand (not on load) — only for visitors who enter a
// money/critical flow, so landing-page bounces aren't recorded. Once per session.
let replayStarted = false
export function startReplay() {
  if (!mixpanelEnabled || replayStarted) return
  try {
    mixpanel.start_session_recording?.()
    replayStarted = true
  } catch (e) {
    /* replay unavailable */
  }
}

export default mixpanel
